import { DEFAULT_FLAG_PURPOSE } from '../schemas/flag';

import type { CreateFlagInput } from './flags';
import type { FlagLifecycle, FlagPurpose, FlagType } from '../schemas/flag';

/** What the create form holds — every field as typed, untrimmed. */
export interface CreateDraft {
    /** Unchosen until the operator picks one; a default here is a key burned. */
    readonly type: FlagType | null;
    readonly key: string;
    /** Ignored unless the flag is a `salesforce` flag. */
    readonly salesforceField: string;
    readonly name: string;
    readonly description: string;
    readonly category: string;
    readonly owner: string;
    readonly lifecycle: FlagLifecycle;
    readonly purpose: FlagPurpose;
    readonly reason: string;
}

export const EMPTY_CREATE_DRAFT: CreateDraft = {
    type: null,
    key: '',
    salesforceField: '',
    name: '',
    description: '',
    category: '',
    owner: '',
    lifecycle: 'dev',
    purpose: DEFAULT_FLAG_PURPOSE,
    reason: '',
};

/**
 * Build a `POST /flags` body from the create form. `null` while the type is
 * unchosen, which is the one state that has no body to send.
 *
 * ## Why this is a module and not four lines inside the dialog
 *
 * `apps/web`'s vitest runs on `environment: 'node'`, so nothing rendered is
 * testable here. A payload built inside `submit()` is a payload no test can
 * see — and this one decides what a permanent, immutable row says. Sitting out
 * here it is provable, and the dialog is left holding only what it draws.
 *
 * ## Why `purpose` is always sent
 *
 * The API applies `release` itself when the key is absent, so omitting it would
 * work. It is sent anyway: the audit entry then records the purpose the operator
 * had on screen rather than the one the server chose, and the two cannot drift
 * apart without someone noticing. The cost is that the default now has two
 * readers — {@link EMPTY_CREATE_DRAFT} here and the API's fallback — and moving
 * it means moving both.
 */
export function createFlagInputFrom(draft: CreateDraft): CreateFlagInput | null {
    if (draft.type === null) return null;

    const reason = draft.reason.trim();

    return {
        key: draft.key.trim(),
        type: draft.type,
        // Only a `salesforce` flag carries one; the API refuses it on
        // `veevart2`, so a value typed before switching type stays in the draft.
        ...(draft.type === 'salesforce' ? { salesforceField: draft.salesforceField.trim() } : {}),
        name: draft.name.trim(),
        description: draft.description.trim(),
        category: draft.category.trim(),
        owner: draft.owner.trim(),
        lifecycle: draft.lifecycle,
        purpose: draft.purpose,
        ...(reason.length > 0 ? { reason } : {}),
    };
}
